import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Chip,
  Avatar,
  Skeleton,
  Card,
  CardContent,
  Divider,
  LinearProgress,
  Stack,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link as MuiLink,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import axios from "axios";
import i18n from "../i18n";
import { getS3Url, ensureDirectS3Url } from "../utils/imageUtils";
import PodscopePublicHeader from "../components/PodscopePublicHeader";
import { productBrand } from "../productBrand";

// Top offset that pairs with the 64px PodscopePublicHeader (matches the
// `mt: { xs: 10, sm: 11, md: 12 }` used by the TonyBet route).
const NAVBAR_OFFSET = { xs: 10, sm: 11, md: 12 } as const;

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

// --- Types matching the public API payload ---

type ScriptType =
  | "read_script"
  | "casual_integration"
  | "host_endorsement"
  | "discount_drop"
  | "interview"
  | "passing_mention";

type Tone =
  | "enthusiastic"
  | "neutral"
  | "skeptical"
  | "informational"
  | "playful";

type HostInvolvement =
  | "personal_endorsement"
  | "third_party_read"
  | "mixed"
  | "unclear";

type TopicAlignment =
  | "natural"
  | "adjacent"
  | "forced"
  | "unrelated"
  | "unclear";

interface BreakdownEntry<T extends string> {
  type: T;
  count: number;
  percent: number;
}

interface MessageCluster {
  id: string;
  label: string;
  description: string;
  sampleMentionIds: string[];
  count: number;
  percent: number;
}

interface AggregatedOffer {
  code?: string;
  url?: string;
  discount?: string;
  freeBet?: string;
  count: number;
}

interface ProgramContextEntry {
  programId: string;
  programName: string;
  podcastId?: string;
  mentionCount: number;
  dominantEpisodeTopics: string[];
  avgRelevance: number;
  alignmentBreakdown: BreakdownEntry<TopicAlignment>[];
}

interface TopQuote {
  mentionId: string;
  quote: string;
  programName?: string;
  publishedAt?: string;
}

interface QualitativeMention {
  _id: string;
  mentionId: string;
  contentId: string;
  programId?: string;
  podcastId: string;
  publishedAt?: string;
  scriptType?: ScriptType;
  valueProps: string[];
  claims: string[];
  ctas: string[];
  offers: AggregatedOffer[];
  tone?: Tone;
  hostInvolvement?: HostInvolvement;
  keyQuote?: string;
  summaryEs?: string;
  episodeTheme?: string;
  topicAlignment?: TopicAlignment;
  topicAlignmentReason?: string;
  contextualRelevanceScore?: number;
  mentionTranscript?: string;
  mentionTranscriptStart?: number;
  mentionTranscriptEnd?: number;
  episodeTopTopics?: Array<{
    topicName: string;
    percentageOfContent?: number;
    durationSeconds?: number;
    confidence?: number;
  }>;
  episodeTopicsAtMention: string[];
  segmentStart?: number;
  segmentEnd?: number;
  mentionText?: string;
  mentionContext?: string;
  episodeTitle?: string;
  episodeUrl?: string;
  episodeSourceId?: string;
  episodeSource?: string;
  episodeThumbnail?: string;
  programName?: string;
  programImageS3Key?: string;
}

interface BrandReportPayload {
  brand: {
    _id: string;
    name: string;
    category?: string;
    logoUrl?: string;
    s3Key?: string;
    description?: string;
    website?: string;
  };
  report: {
    generatedAt: string;
    dateRange: { from?: string; to?: string };
    totals: { mentions: number; programs: number; episodes: number };
    consistencyScore: number | null;
    dominantScriptType: ScriptType | null;
    messageClusters: MessageCluster[];
    scriptTypeBreakdown: BreakdownEntry<ScriptType>[];
    toneBreakdown: BreakdownEntry<Tone>[];
    hostInvolvementBreakdown: BreakdownEntry<HostInvolvement>[];
    topicAlignmentBreakdown: BreakdownEntry<TopicAlignment>[];
    offers: AggregatedOffer[];
    programContext: ProgramContextEntry[];
    topQuotes: TopQuote[];
    narrativeEs: string;
  };
  mentions: QualitativeMention[];
}

function scriptTypeLabels(): Record<ScriptType, string> {
  return {
    read_script: i18n.t("publicReports:scriptType.read_script"),
    casual_integration: i18n.t("publicReports:scriptType.casual_integration"),
    host_endorsement: i18n.t("publicReports:scriptType.host_endorsement"),
    discount_drop: i18n.t("publicReports:scriptType.discount_drop"),
    interview: i18n.t("publicReports:scriptType.interview"),
    passing_mention: i18n.t("publicReports:scriptType.passing_mention"),
  };
}

function toneLabels(): Record<Tone, string> {
  return {
    enthusiastic: i18n.t("publicReports:tone.enthusiastic"),
    neutral: i18n.t("publicReports:tone.neutral"),
    skeptical: i18n.t("publicReports:tone.skeptical"),
    informational: i18n.t("publicReports:tone.informational"),
    playful: i18n.t("publicReports:tone.playful"),
  };
}

function hostInvolvementLabels(): Record<HostInvolvement, string> {
  return {
    personal_endorsement: i18n.t("publicReports:host.personal_endorsement"),
    third_party_read: i18n.t("publicReports:host.third_party_read"),
    mixed: i18n.t("publicReports:host.mixed"),
    unclear: i18n.t("publicReports:host.unclear"),
  };
}

function alignmentLabels(): Record<TopicAlignment, string> {
  return {
    natural: i18n.t("publicReports:alignment.natural"),
    adjacent: i18n.t("publicReports:alignment.adjacent"),
    forced: i18n.t("publicReports:alignment.forced"),
    unrelated: i18n.t("publicReports:alignment.unrelated"),
    unclear: i18n.t("publicReports:alignment.unclear"),
  };
}

const ALIGNMENT_COLOR: Record<TopicAlignment, string> = {
  natural: "#2e7d32",
  adjacent: "#558b2f",
  forced: "#ed6c02",
  unrelated: "#c62828",
  unclear: "#757575",
};

// --- Helpers ---

function formatDate(s?: string): string {
  if (!s) return "—";
  try {
    const locale = i18n.language?.startsWith("en") ? "en-US" : "es-CL";
    return new Date(s).toLocaleDateString(locale, {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return s;
  }
}

function formatPercent(p: number, digits = 0): string {
  return `${(p * 100).toFixed(digits)}%`;
}

function youtubeDeepLink(
  source: string | undefined,
  sourceId: string | undefined,
  url: string | undefined,
  startSeconds: number | undefined,
): string | undefined {
  const seconds = Math.max(0, Math.floor(startSeconds || 0));
  if (source === "youtube" && sourceId) {
    return `https://www.youtube.com/watch?v=${sourceId}${seconds ? `&t=${seconds}s` : ""}`;
  }
  if (url && url.includes("youtube.com") && seconds) {
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}t=${seconds}s`;
  }
  return url;
}

// Consistency = Herfindahl index of cluster shares. Reads:
//   1.00 = un único mensaje
//   0.75 = mensaje muy concentrado (ej: 85/15)
//   0.50 = un eje dominante con mensajes secundarios (ej: 70/30)
//   0.33 = tres ejes equilibrados
//   ≤0.25 = cuatro o más ejes, ninguno claramente dominante
function consistencyLabel(score: number | null): string {
  if (score === null || score === undefined) return "—";
  if (score >= 0.85) return i18n.t("publicReports:consistencyLabel.single");
  if (score >= 0.6)
    return i18n.t("publicReports:consistencyLabel.veryConsistent");
  if (score >= 0.45) return i18n.t("publicReports:consistencyLabel.dominant");
  if (score >= 0.3)
    return i18n.t("publicReports:consistencyLabel.multidimensional");
  return i18n.t("publicReports:consistencyLabel.fragmented");
}

function consistencyColor(score: number | null): string {
  if (score === null || score === undefined) return "#757575";
  if (score >= 0.85) return "#2e7d32";
  if (score >= 0.6) return "#558b2f";
  if (score >= 0.45) return "#9e9d24";
  if (score >= 0.3) return "#ed6c02";
  return "#c62828";
}

function translateTopic(name: string): string {
  const trimmed = name.trim();
  return i18n.t(`topics.${trimmed}`, {
    ns: "publicReports",
    defaultValue: trimmed,
  });
}

/**
 * Backend Phase B sends per-program dominant topics as preformatted strings like
 * `"Music (45%)"` or just `"Music"`. Parse them so the UI can translate the
 * topic name to Spanish and show the share separately.
 */
function parseDominantTopic(s: string): { topic: string; pct: number | null } {
  const m = s.match(/^(.+?)\s*\((\d+)%\)\s*$/);
  if (m) return { topic: m[1].trim(), pct: parseInt(m[2], 10) };
  return { topic: s.trim(), pct: null };
}

// --- Subcomponents ---

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sublabel?: string;
}> = ({ label, value, sublabel }) => (
  <Card variant="outlined" sx={{ height: "100%" }}>
    <CardContent>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700 }}
      >
        {label}
      </Typography>
      <Typography
        variant="h4"
        sx={{ fontWeight: 800, mt: 0.5, lineHeight: 1.1 }}
      >
        {value}
      </Typography>
      {sublabel && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {sublabel}
        </Typography>
      )}
    </CardContent>
  </Card>
);

function BreakdownBar<T extends string>(props: {
  title: string;
  entries: BreakdownEntry<T>[];
  labelMap: Record<T, string>;
  colorMap?: Partial<Record<T, string>>;
}) {
  const { t } = useTranslation("publicReports");
  const { title, entries, labelMap, colorMap } = props;
  const fallbackPalette = [
    "#1976d2",
    "#2e7d32",
    "#ed6c02",
    "#9c27b0",
    "#0288d1",
    "#c62828",
  ];
  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
          {title}
        </Typography>
        {entries.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            {t("noData")}
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {entries.map((e, i) => {
              const color =
                colorMap?.[e.type] ||
                fallbackPalette[i % fallbackPalette.length];
              return (
                <Box key={e.type}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.25,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {labelMap[e.type] || e.type}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {e.count} · {formatPercent(e.percent)}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={e.percent * 100}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: "rgba(0,0,0,0.06)",
                      "& .MuiLinearProgress-bar": { bgcolor: color },
                    }}
                  />
                </Box>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

const StackedClusterBar: React.FC<{ clusters: MessageCluster[] }> = ({
  clusters,
}) => {
  const palette = [
    "#1976d2",
    "#2e7d32",
    "#ed6c02",
    "#9c27b0",
    "#0288d1",
    "#c62828",
    "#5d4037",
  ];
  if (clusters.length === 0) return null;
  return (
    <Box
      sx={{
        display: "flex",
        height: 32,
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        mb: 2,
      }}
    >
      {clusters.map((c, i) => (
        <Tooltip
          key={c.id}
          title={`${c.label} · ${c.count} (${formatPercent(c.percent)})`}
          arrow
        >
          <Box
            sx={{
              width: `${c.percent * 100}%`,
              bgcolor: palette[i % palette.length],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontSize: "0.75rem",
              fontWeight: 700,
              minWidth: c.percent < 0.04 ? 4 : "auto",
            }}
          >
            {c.percent >= 0.08 ? formatPercent(c.percent) : ""}
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
};

// --- Page ---

const BrandQualitativeReport: React.FC = () => {
  const { t } = useTranslation("publicReports");
  const { brandId } = useParams<{ brandId: string }>();
  const [openCluster, setOpenCluster] = useState<string | false>(false);

  const { data, isLoading, error } = useQuery<BrandReportPayload>({
    queryKey: ["brand-qualitative-report", brandId],
    queryFn: async () => {
      const resp = await axios.get<BrandReportPayload>(
        `${API_BASE_URL}/api/public/brand-reports/${brandId}`,
      );
      return resp.data;
    },
    enabled: !!brandId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const mentionsById = useMemo(() => {
    const m = new Map<string, QualitativeMention>();
    data?.mentions.forEach((mn) => m.set(mn.mentionId, mn));
    return m;
  }, [data]);

  const sortedMentions = useMemo(() => {
    if (!data) return [];
    return [...data.mentions].sort((a, b) => {
      const da = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
      const db = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
      return da - db;
    });
  }, [data]);

  if (isLoading) {
    return (
      <>
        <PodscopePublicHeader />
        <Container maxWidth="lg" sx={{ pt: NAVBAR_OFFSET, pb: 6 }}>
          <Skeleton
            variant="rectangular"
            height={120}
            sx={{ mb: 3, borderRadius: 2 }}
          />
          <Skeleton
            variant="rectangular"
            height={200}
            sx={{ mb: 3, borderRadius: 2 }}
          />
          <Skeleton
            variant="rectangular"
            height={400}
            sx={{ borderRadius: 2 }}
          />
        </Container>
      </>
    );
  }

  if (error || !data) {
    const status = (error as any)?.response?.status;
    const msg =
      status === 404
        ? t("notGenerated")
        : t("loadError");
    return (
      <>
        <PodscopePublicHeader />
        <Container maxWidth="md" sx={{ pt: NAVBAR_OFFSET, pb: 8 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                {t("unavailable")}
              </Typography>
              <Typography color="text.secondary">{msg}</Typography>
            </CardContent>
          </Card>
        </Container>
      </>
    );
  }

  const { brand, report, mentions } = data;
  const logoSrc = brand.logoUrl
    ? brand.logoUrl
    : brand.s3Key
      ? ensureDirectS3Url(getS3Url(brand.s3Key))
      : undefined;

  const fromLabel = formatDate(report.dateRange?.from);
  const toLabel = formatDate(report.dateRange?.to);

  return (
    <>
      <PodscopePublicHeader />
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          pt: NAVBAR_OFFSET,
          pb: { xs: 4, md: 6 },
        }}
      >
        <Container maxWidth="lg">
          {/* 1. Header */}
          <Card variant="outlined" sx={{ mb: 4 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <Avatar
                  src={logoSrc}
                  alt={brand.name}
                  variant="rounded"
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: "grey.100",
                    color: "text.primary",
                    fontWeight: 700,
                    fontSize: 32,
                  }}
                >
                  {brand.name?.[0]?.toUpperCase() || "?"}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 240 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: 1.2, fontWeight: 700 }}
                  >
                    {t("overline")}
                  </Typography>
                  <Typography
                    component="h1"
                    sx={{
                      fontSize: { xs: "1.75rem", md: "2.5rem" },
                      fontWeight: 800,
                      lineHeight: 1.1,
                    }}
                  >
                    {brand.name}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mt: 1, flexWrap: "wrap", rowGap: 1 }}
                  >
                    {brand.category && (
                      <Chip label={brand.category} size="small" />
                    )}
                    <Chip
                      label={`${fromLabel} → ${toLabel}`}
                      size="small"
                      variant="outlined"
                    />
                  </Stack>
                </Box>
                <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
                  <Typography variant="caption" color="text.secondary">
                    {t("generatedOn")}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatDate(report.generatedAt)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* 2. Quick stats */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid size={{ xs: 6, md: 3 }}>
              <StatCard label={t("mentions")} value={report.totals.mentions} />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <StatCard label={t("programs")} value={report.totals.programs} />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <StatCard label={t("episodes")} value={report.totals.episodes} />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <StatCard
                label={t("consistency")}
                value={
                  <Box
                    component="span"
                    sx={{ color: consistencyColor(report.consistencyScore) }}
                  >
                    {report.consistencyScore !== null
                      ? `${(report.consistencyScore * 100).toFixed(0)}%`
                      : "—"}
                  </Box>
                }
                sublabel={consistencyLabel(report.consistencyScore)}
              />
            </Grid>
          </Grid>

          {/* 3. Mensaje de la marca */}
          <Card variant="outlined" sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                {t("sameMessage")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("messageDistribution", { brand: brand.name })}
              </Typography>

              <StackedClusterBar clusters={report.messageClusters} />

              {report.messageClusters.length === 0 ? (
                <Typography color="text.secondary">
                  {t("noClusters")}
                </Typography>
              ) : (
                <Box>
                  {report.messageClusters.map((cluster) => (
                    <Accordion
                      key={cluster.id}
                      expanded={openCluster === cluster.id}
                      onChange={(_, isOpen) =>
                        setOpenCluster(isOpen ? cluster.id : false)
                      }
                      disableGutters
                      elevation={0}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        mb: 1,
                        "&:before": { display: "none" },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Chip
                            label={`${cluster.count}`}
                            size="small"
                            sx={{ fontWeight: 700, minWidth: 36 }}
                          />
                          <Typography sx={{ fontWeight: 700 }}>
                            {cluster.label}
                          </Typography>
                          <Typography color="text.secondary" variant="body2">
                            · {formatPercent(cluster.percent)}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography color="text.secondary" sx={{ mb: 2 }}>
                          {cluster.description}
                        </Typography>
                        <Stack spacing={1.5}>
                          {cluster.sampleMentionIds.map((mid) => {
                            const m = mentionsById.get(mid);
                            if (!m) return null;
                            const link = youtubeDeepLink(
                              m.episodeSource,
                              m.episodeSourceId,
                              m.episodeUrl,
                              m.segmentStart,
                            );
                            return (
                              <Box
                                key={mid}
                                sx={{
                                  p: 1.5,
                                  bgcolor: (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "rgba(255,255,255,0.04)"
                                      : "grey.50",
                                  borderRadius: 1.5,
                                  border: "1px solid",
                                  borderColor: "divider",
                                }}
                              >
                                {m.keyQuote && (
                                  <Typography
                                    sx={{ fontStyle: "italic", mb: 0.5 }}
                                  >
                                    «{m.keyQuote}»
                                  </Typography>
                                )}
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {m.programName || t("programFallback")}{" "}
                                  {m.publishedAt &&
                                    `· ${formatDate(m.publishedAt)}`}
                                  {link && (
                                    <>
                                      {" · "}
                                      <MuiLink
                                        href={link}
                                        target="_blank"
                                        rel="noreferrer"
                                        underline="hover"
                                      >
                                        {t("viewInEpisode")}{" "}
                                        <OpenInNewIcon
                                          sx={{
                                            fontSize: 14,
                                            verticalAlign: "middle",
                                          }}
                                        />
                                      </MuiLink>
                                    </>
                                  )}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* 4. Cómo lo dicen — formato y tono */}
          <Card variant="outlined" sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                {t("howTheySay")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t("howTheySayBody")}
              </Typography>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <BreakdownBar
                    title={t("scriptTypeTitle")}
                    entries={report.scriptTypeBreakdown}
                    labelMap={scriptTypeLabels()}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <BreakdownBar
                    title={t("toneTitle")}
                    entries={report.toneBreakdown}
                    labelMap={toneLabels()}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <BreakdownBar
                    title={t("hostTitle")}
                    entries={report.hostInvolvementBreakdown}
                    labelMap={hostInvolvementLabels()}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* 5. Contexto del programa */}
          <Card variant="outlined" sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                {t("programContext")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t("programContextBody")}
              </Typography>

              <Box sx={{ mb: 3 }}>
                <BreakdownBar
                  title={t("alignmentTitle")}
                  entries={report.topicAlignmentBreakdown}
                  labelMap={alignmentLabels()}
                  colorMap={ALIGNMENT_COLOR}
                />
              </Box>

              {report.programContext.length === 0 ? (
                <Typography color="text.secondary">
                  {t("noProgramInfo")}
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {report.programContext.map((p) => (
                    <Box
                      key={p.programId}
                      sx={{
                        p: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Typography sx={{ fontWeight: 700 }}>
                          {p.programName}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            label={t("mentionCount", { count: p.mentionCount })}
                            size="small"
                          />
                          <Chip
                            label={t("relevance", {
                              percent: (p.avgRelevance * 100).toFixed(0),
                            })}
                            size="small"
                            color={
                              p.avgRelevance >= 0.6
                                ? "success"
                                : p.avgRelevance >= 0.35
                                  ? "warning"
                                  : "default"
                            }
                            variant="outlined"
                          />
                        </Stack>
                      </Box>
                      {p.dominantEpisodeTopics.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                              fontWeight: 700,
                            }}
                          >
                            {t("dominantTopics")}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={0.75}
                            sx={{ flexWrap: "wrap", rowGap: 0.5, mt: 0.5 }}
                          >
                            {p.dominantEpisodeTopics.map((raw) => {
                              const { topic, pct } = parseDominantTopic(raw);
                              const label =
                                pct !== null
                                  ? `${translateTopic(topic)} · ${pct}%`
                                  : translateTopic(topic);
                              return (
                                <Chip
                                  key={raw}
                                  label={label}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontWeight: 500 }}
                                />
                              );
                            })}
                          </Stack>
                        </Box>
                      )}
                      {p.alignmentBreakdown.length > 0 && (
                        <Stack
                          direction="row"
                          spacing={0.75}
                          sx={{ flexWrap: "wrap", rowGap: 0.5 }}
                        >
                          {p.alignmentBreakdown.map((a) => (
                            <Chip
                              key={a.type}
                              size="small"
                              label={`${alignmentLabels()[a.type]}: ${a.count}`}
                              sx={{
                                bgcolor: ALIGNMENT_COLOR[a.type],
                                color: "#fff",
                                fontWeight: 600,
                              }}
                            />
                          ))}
                        </Stack>
                      )}
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* 6. Línea editorial (resumen) */}
          {report.narrativeEs && (
            <Card variant="outlined" sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                  {t("editorial")}
                </Typography>
                <Box>
                  {report.narrativeEs
                    .split(/\n+/)
                    .filter(Boolean)
                    .map((para, i) => (
                      <Typography key={i} sx={{ mb: 1.5, lineHeight: 1.7 }}>
                        {para}
                      </Typography>
                    ))}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* 7. Cronología de menciones */}
          <Card variant="outlined" sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                {t("timeline")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t("timelineBody", { count: mentions.length })}
              </Typography>

              <Stack spacing={1.5}>
                {sortedMentions.map((m) => {
                  const link = youtubeDeepLink(
                    m.episodeSource,
                    m.episodeSourceId,
                    m.episodeUrl,
                    m.segmentStart,
                  );
                  return (
                    <Box
                      key={m._id}
                      sx={{
                        display: "flex",
                        gap: 2,
                        p: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        alignItems: "flex-start",
                      }}
                    >
                      <Box sx={{ minWidth: 90 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 700 }}
                        >
                          {formatDate(m.publishedAt)}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 700, mb: 0.25 }}>
                          {m.programName || t("programFallback")}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 0.75 }}
                        >
                          {m.episodeTitle || t("untitledEpisode")}
                        </Typography>

                        {/* What the episode was talking about around the mention */}
                        {m.episodeTheme && (
                          <Box
                            sx={{
                              p: 1.25,
                              mb: 1,
                              bgcolor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.04)"
                                  : "grey.50",
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                                fontWeight: 700,
                                display: "block",
                                mb: 0.25,
                              }}
                            >
                              {t("episodeAbout")}
                            </Typography>
                            <Typography variant="body2">
                              {m.episodeTheme}
                            </Typography>
                            {m.episodeTopTopics &&
                              m.episodeTopTopics.length > 0 && (
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  sx={{
                                    mt: 0.75,
                                    flexWrap: "wrap",
                                    rowGap: 0.5,
                                  }}
                                >
                                  {m.episodeTopTopics.map((t) => {
                                    const pct = Math.round(
                                      t.percentageOfContent || 0,
                                    );
                                    const label = translateTopic(t.topicName);
                                    return (
                                      <Chip
                                        key={t.topicName}
                                        size="small"
                                        label={
                                          pct > 0 ? `${label} · ${pct}%` : label
                                        }
                                        variant="outlined"
                                        sx={{ fontWeight: 500 }}
                                      />
                                    );
                                  })}
                                </Stack>
                              )}
                          </Box>
                        )}

                        {m.keyQuote && (
                          <Typography sx={{ fontStyle: "italic", mb: 0.75 }}>
                            «{m.keyQuote}»
                          </Typography>
                        )}
                        {m.summaryEs && (
                          <Typography variant="body2" sx={{ mb: 0.75 }}>
                            {m.summaryEs}
                          </Typography>
                        )}

                        {/* Why the mention does/doesn't fit the episode */}
                        {m.topicAlignmentReason && (
                          <Box
                            sx={{
                              p: 1.25,
                              mb: 1,
                              borderLeft: "3px solid",
                              borderColor: m.topicAlignment
                                ? ALIGNMENT_COLOR[m.topicAlignment]
                                : "divider",
                              bgcolor: "background.paper",
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                                fontWeight: 700,
                                display: "block",
                                mb: 0.25,
                              }}
                            >
                              {t("fitsTopic")}
                            </Typography>
                            <Typography variant="body2">
                              {m.topicAlignmentReason}
                            </Typography>
                          </Box>
                        )}

                        <Stack
                          direction="row"
                          spacing={0.75}
                          sx={{ flexWrap: "wrap", rowGap: 0.5 }}
                        >
                          {m.scriptType && (
                            <Chip
                              size="small"
                              label={scriptTypeLabels()[m.scriptType]}
                            />
                          )}
                          {m.tone && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={toneLabels()[m.tone]}
                            />
                          )}
                          {m.topicAlignment && (
                            <Chip
                              size="small"
                              label={alignmentLabels()[m.topicAlignment]}
                              sx={{
                                bgcolor: ALIGNMENT_COLOR[m.topicAlignment],
                                color: "#fff",
                                fontWeight: 600,
                              }}
                            />
                          )}
                          {link && (
                            <Chip
                              component="a"
                              href={link}
                              target="_blank"
                              rel="noreferrer"
                              clickable
                              size="small"
                              icon={<OpenInNewIcon />}
                              label={t("viewOnEpisode")}
                            />
                          )}
                        </Stack>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>

          {/* 8. Top quotes */}
          {report.topQuotes.length > 0 && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                  {t("topQuotes")}
                </Typography>
                <Stack spacing={2}>
                  {report.topQuotes.map((q) => (
                    <Box
                      key={q.mentionId}
                      sx={{
                        borderLeft: "4px solid",
                        borderColor: "primary.main",
                        pl: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          fontStyle: "italic",
                          fontSize: "1.1rem",
                          mb: 0.5,
                        }}
                      >
                        «{q.quote}»
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {q.programName || t("programFallback")} ·{" "}
                        {formatDate(q.publishedAt)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}

          <Divider sx={{ my: 4 }} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", textAlign: "center" }}
          >
            {t("footer", { brand: productBrand.name })}
          </Typography>
        </Container>
      </Box>
    </>
  );
};

export default BrandQualitativeReport;
